import React, { useState, useEffect, useRef } from "react";
import { X, Upload } from "lucide-react";
import { API_BASE_URL } from "../../../config/api";
import axios from "axios";
import { toHttps } from "../../../utils/imageUtils";
import AccessoryTab from "./AccessoryTab";
import SizeChartSection from "./SizeChartSection";
import MeasurementBuilder from "./MeasurementBuilder";
import { Ruler } from "lucide-react";

const EditModal = ({ product, onClose, onSave }) => {
  // State Management
  const [formData, setFormData] = useState({
    _id: product._id,
    productName: product.productName || "",
    productCode: product.productCode || "",
    categoryId: product.categoryId?._id || "",
    categoryName: product.categoryName || "",
    variantName: product.variantName || "",
    description: product.description || "",
    details: product.details || "",
    actualPrice: product.actualPrice || "",
    sellingPrice: product.sellingPrice || "",
    tax: product.tax || "",
    color: product.color || "",
    colorImages: product.colorImages || [],
    images: product.images || [],
    tags: product.tags || [],
    stock: product.stock || "",
    sizeAndFit: product.sizeAndFit || "FREE SIZE",
    availability: product.availability ?? true,
    sareeSize: product.sareeSize || 5.5,
    blouseSize: product.blouseSize || 0.8,
    weight: product.weight || "",
    materialAndCare: product.materialAndCare || "",
  });

  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImages, setPreviewImages] = useState(product.images || []);
  const [colorImages, setColorImages] = useState(
    (product.colorImages || []).map((item) => {
      const existing = [];
      if (Array.isArray(item.images)) existing.push(...item.images);
      if (item.image) existing.push(item.image);
      const unique = existing.filter(Boolean).map((url) => toHttps(url));
      return {
        color: item.color || "",
        images: unique.map((url) => ({
          url,
          file: null,
          preview: url,
        })),
      };
    })
  );
  const [categories, setCategories] = useState([]);
  const [variants, setVariants] = useState([]);
  const descriptionRef = useRef(null);
  const [isPriceSame, setIsPriceSame] = useState(
    product.actualPrice === product.sellingPrice
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const applyDescriptionFormat = (command, value = null) => {
    document.execCommand(command, false, value);
  };

  // State for selected product size (for non-saree products)
  const [selectedProductSizes, setSelectedProductSizes] = useState(product.availableSizes || []);
  const [displaySizeTable, setDisplaySizeTable] = useState(product.displaySizeTable ?? false);
  const [sizeChartHeaders, setSizeChartHeaders] = useState(product.sizeChartHeaders || []);
  const [sizeChartData, setSizeChartData] = useState(product.sizeChartData || []);
  const [measurements, setMeasurements] = useState(product.measurements || []);
  const [showMeasurementBuilder, setShowMeasurementBuilder] = useState(false);

  // Fetch Data
  useEffect(() => {
    fetchCategories();
    fetchVariants();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/categories/allcategory`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const fetchVariants = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/variants/getallvariants`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setVariants(data);
    } catch (error) {
      console.error("Failed to fetch variants:", error);
    }
  };

  // Image Handling
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0 && previewImages.length < 6) {
      files.slice(0, 6 - previewImages.length).forEach((file) => {
        if (file.type.startsWith("image/")) {
          const imageUrl = URL.createObjectURL(file);
          setPreviewImages((prev) => [...prev, imageUrl]);
          setSelectedImage(file);
        } else {
          alert("Please select an image file");
        }
      });
    }
  };

  const handleRemoveImage = (index) => {
    setPreviewImages((prev) => prev.filter((_, i) => i !== index));
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0 && previewImages.length < 6) {
      files.slice(0, 6 - previewImages.length).forEach((file) => {
        if (file.type.startsWith("image/")) {
          const imageUrl = URL.createObjectURL(file);
          setPreviewImages((prev) => [...prev, imageUrl]);
          setSelectedImage(file);
        } else {
          alert("Please drop an image file");
        }
      });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const uploadImage = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post("/upload", formData, {
        baseURL: API_BASE_URL,
        headers: {
          Authorization: "QuindlTokPATFileUpload2025#$$TerOiu$",
          "Content-Type": "multipart/form-data",
        },
      });

      if (!response.data || !response.data.filePath) {
        throw new Error("Invalid response from image upload");
      }

      // Convert HTTP URLs to HTTPS before returning
      return toHttps(response.data.filePath);
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    }
  };

  const handleAddColorImage = () => {
    setColorImages((prev) => [...prev, { color: "", images: [] }]);
  };

  const handleRemoveColorImage = (index) => {
    setColorImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleColorImageChange = (index, value) => {
    setColorImages((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, color: value } : item
      )
    );
  };

  const handleColorImageSelect = (index, files) => {
    const fileList = Array.from(files || []).filter((f) =>
      f.type.startsWith("image/")
    );
    if (fileList.length === 0) {
      alert("Please select an image file");
      return;
    }

    setColorImages((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        const currentCount = item.images?.length || 0;
        const remaining = Math.max(0, 5 - currentCount);
        if (remaining === 0) {
          alert("You can upload up to 5 images per color");
          return item;
        }
        const selected = fileList.slice(0, remaining);
        const newImages = selected.map((file) => ({
          url: "",
          file,
          preview: URL.createObjectURL(file),
        }));
        return { ...item, images: [...(item.images || []), ...newImages] };
      })
    );
  };

  const handleRemoveColorImageFile = (colorIndex, imageIndex) => {
    setColorImages((prev) =>
      prev.map((item, i) => {
        if (i !== colorIndex) return item;
        return {
          ...item,
          images: (item.images || []).filter((_, idx) => idx !== imageIndex),
        };
      })
    );
  };

  // Form Handling
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Submit triggered. Current measurements state:", measurements);
    alert("DEBUG: Saving product with " + measurements.length + " measurement tables.");
    try {
      if (isSaving) return;
      setIsSaving(true);
      setSaveError("");
      let imageUrls = [...formData.images];

      if (selectedImage) {
        const newImageUrl = await uploadImage(selectedImage);
        imageUrls = [...imageUrls, newImageUrl];
      }

      const colorImagesPayload = [];
      for (const entry of colorImages) {
        const colorName = entry.color?.trim();
        const images = Array.isArray(entry.images) ? entry.images : [];
        if (!colorName && images.length === 0) continue;

        const uploadedImages = [];
        for (const img of images) {
          if (img?.file) {
            const url = await uploadImage(img.file);
            if (url) uploadedImages.push(url);
          } else if (img?.url) {
            uploadedImages.push(img.url);
          }
        }

        if (colorName && uploadedImages.length > 0) {
          colorImagesPayload.push({
            color: colorName,
            image: uploadedImages[0],
            images: uploadedImages,
          });
        }
      }

      const updatedProduct = {
        ...formData,
        images: imageUrls,
        colorImages: colorImagesPayload,
        categoryId: formData.categoryId,
        categoryName:
          categories.find((cat) => cat._id === formData.categoryId)
            ?.categoryName || formData.categoryName,
        variantName: formData.variantName,
        actualPrice: Number(formData.actualPrice),
        sellingPrice: Number(formData.sellingPrice),
        tax: Number(formData.tax),
        stock: Number(formData.stock),
        availability: Boolean(formData.availability),
        sareeSize: Number(formData.sareeSize),
        blouseSize: Number(formData.blouseSize),
        weight: Number(formData.weight),
        materialAndCare: formData.materialAndCare.trim(),
        tags: Array.isArray(formData.tags) ? formData.tags : [],
        sizeAndFit: formData.sizeAndFit,
        availableSizes: selectedProductSizes,
        displaySizeTable: displaySizeTable,
        sizeChartHeaders: sizeChartHeaders,
        sizeChartData: sizeChartData,
        measurements: measurements,
      };

      // Make the API call to update the product
      const response = await fetch(
        `${API_BASE_URL}/products/editproduct/${formData._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedProduct),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update product");
      }

      const result = await response.json();

      // Call the onSave callback with the updated product
      await onSave(result);
      onClose();
    } catch (error) {
      console.error("Error updating product:", error);
      const message = error?.message || "Failed to update product";
      setSaveError(message);
      alert(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "actualPrice" && isPriceSame) {
      setFormData((prev) => ({
        ...prev,
        sellingPrice: value,
      }));
    }
  };

  // Add this function to check if selected category is Saree
  const isSelectedCategorySaree = () => {
    const selectedCategory = categories.find(
      (cat) => cat._id === formData.categoryId
    );
    return selectedCategory?.categoryType === "Saree";
  };

  // Render Component
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-[90%] max-w-6xl my-8 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white px-6 py-4 border-b flex justify-between items-center">
          <h2 className="text-2xl font-semibold text-gray-800">Edit Product</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <form
            id="edit-product-form"
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            {/* Image Upload Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Images ({previewImages.length}/6)
              </label>
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-6"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                <div className="grid grid-cols-3 gap-4">
                  {previewImages.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={image}
                        alt={`Product ${index + 1}`}
                        className="w-full h-40 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>
                  ))}

                  {previewImages.length < 6 && (
                    <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-4 cursor-pointer hover:bg-gray-50">
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-500">Upload Image</p>
                      <p className="text-xs text-gray-400">
                        Drop your image here or click to browse
                      </p>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleImageSelect}
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>

            {/* Color Image Mapping Section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Colour Image Mapping
                </label>
                <button
                  type="button"
                  onClick={handleAddColorImage}
                  className="px-3 py-1 text-xs font-medium rounded-md border border-purple-200 text-purple-700 hover:bg-purple-50"
                >
                  Add Colour
                </button>
              </div>
              <div className="space-y-3">
                {colorImages.length === 0 && (
                  <p className="text-xs text-gray-500">
                    Add a colour name and upload the matching image.
                  </p>
                )}
                {colorImages.map((item, index) => (
                  <div
                    key={`color-image-${index}`}
                    className="flex flex-wrap items-center gap-3 border border-gray-200 rounded-lg p-3"
                  >
                    <input
                      type="text"
                      value={item.color}
                      onChange={(e) =>
                        handleColorImageChange(index, e.target.value)
                      }
                      placeholder="e.g. Black"
                      className="flex-1 min-w-[160px] px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        {(item.images || []).length === 0 && (
                          <div className="w-16 h-16 rounded-md border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center">
                            <span className="text-[10px] text-gray-400 text-center px-1">
                              No image
                            </span>
                          </div>
                        )}
                        {(item.images || []).map((img, imgIndex) => (
                          <div
                            key={`edit-color-${index}-${imgIndex}`}
                            className="relative w-16 h-16 rounded-md border border-gray-200 bg-gray-50 overflow-hidden"
                          >
                            <img
                              src={img.preview || img.url}
                              alt={item.color || "Colour preview"}
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                handleRemoveColorImageFile(index, imgIndex)
                              }
                              className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-white border border-gray-200 text-xs text-gray-600 hover:text-red-600"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                      <label className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-md text-xs text-gray-600 cursor-pointer hover:bg-gray-50">
                        <Upload className="w-4 h-4" />
                        Upload
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          multiple
                          onChange={(e) =>
                            handleColorImageSelect(index, e.target.files)
                          }
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => handleRemoveColorImage(index)}
                        className="p-2 rounded-md border border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-200"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Basic Information
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Product Name
                    </label>
                    <input
                      type="text"
                      name="productName"
                      value={formData.productName}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Product Code
                    </label>
                    <input
                      type="text"
                      name="productCode"
                      value={formData.productCode}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      name="categoryId"
                      value={formData.categoryId}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Select Category</option>
                      {categories.map((category) => (
                        <option key={category._id} value={category._id}>
                          {category.categoryName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Variant
                    </label>
                    <select
                      name="variantName"
                      value={formData.variantName}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Select Variant</option>
                      {variants.map((variant) => (
                        <option key={variant._id} value={variant.variantName}>
                          {variant.variantName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Pricing Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Pricing</h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cost Price
                    </label>
                    <input
                      type="number"
                      name="actualPrice"
                      value={formData.actualPrice}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Selling Price
                    </label>
                    <input
                      type="number"
                      name="sellingPrice"
                      value={formData.sellingPrice}
                      onChange={handleChange}
                      disabled={isPriceSame}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isPriceSame}
                      onChange={(e) => {
                        setIsPriceSame(e.target.checked);
                        if (e.target.checked) {
                          setFormData((prev) => ({
                            ...prev,
                            sellingPrice: prev.actualPrice,
                          }));
                        }
                      }}
                      className="rounded border-gray-300"
                    />
                    <label className="text-sm text-gray-600">
                      Same as Cost Price
                    </label>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                {/* Stock & Availability */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Stock & Availability
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stock
                    </label>
                    <input
                      type="number"
                      name="stock"
                      value={formData.stock}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Size and Fit
                    </label>
                    <input
                      type="text"
                      name="sizeAndFit"
                      value={formData.sizeAndFit}
                      onChange={handleChange}
                      placeholder="FREE SIZE"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Availability
                    </label>
                    <select
                      name="availability"
                      value={formData.availability}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value={true}>In Stock</option>
                      <option value={false}>Out of Stock</option>
                    </select>
                  </div>
                </div>

                {/* Additional Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Additional Details
                  </h3>

                  {/* Only show saree and blouse size if category is Saree */}
                  {isSelectedCategorySaree() && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Saree Size (meters)
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          name="sareeSize"
                          value={formData.sareeSize}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Blouse Size (meters)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          name="blouseSize"
                          value={formData.blouseSize}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Product Weight (grams)
                    </label>
                    <input
                      type="number"
                      step="1"
                      name="weight"
                      value={formData.weight}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tax (%)
                    </label>
                    <input
                      type="number"
                      name="tax"
                      value={formData.tax}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Color
                    </label>
                    <input
                      type="text"
                      name="color"
                      value={formData.color}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Wash & Care
                    </label>
                    <textarea
                      name="materialAndCare"
                      value={formData.materialAndCare}
                      onChange={handleChange}
                      rows="4"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Enter wash & care instructions"
                    />
                  </div>

                  {/* Size Chart Section - Only show for non-saree products */}
                  {!isSelectedCategorySaree() && formData.categoryId && (
                    <SizeChartSection
                      selectedSizes={selectedProductSizes}
                      onSizeChange={setSelectedProductSizes}
                      displaySizeTable={displaySizeTable}
                      onDisplayToggle={setDisplaySizeTable}
                      sizeChartHeaders={sizeChartHeaders}
                      sizeChartData={sizeChartData}
                      onSizeChartChange={({ headers, data }) => {
                        setSizeChartHeaders(headers);
                        setSizeChartData(data);
                      }}
                    />
                  )}

                  {/* Measurement Chart Builder */}
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); setShowMeasurementBuilder(true); }}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-medium transition-colors text-sm"
                    >
                      <Ruler className="w-4 h-4" />
                      Set Measurement Chart (How to Measure)
                      {measurements.length > 0 && (
                        <span className="ml-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                          {measurements.length} table{measurements.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </button>
                  </div>

                  {showMeasurementBuilder && (
                    <MeasurementBuilder
                      measurements={measurements}
                      onChange={setMeasurements}
                      onClose={() => setShowMeasurementBuilder(false)}
                    />
                  )}


                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                    <div className="mb-2 rounded-lg border border-purple-200 bg-purple-50/60 p-2">
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="text-gray-600">Format</span>
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => applyDescriptionFormat("bold")}
                          className="px-2 py-1 rounded-md border text-xs font-semibold bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                          aria-label="Bold"
                        >
                          B
                        </button>
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => applyDescriptionFormat("italic")}
                          className="px-2 py-1 rounded-md border text-xs font-semibold italic bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                          aria-label="Italic"
                        >
                          I
                        </button>
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => applyDescriptionFormat("underline")}
                          className="px-2 py-1 rounded-md border text-xs font-semibold underline bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                          aria-label="Underline"
                        >
                          U
                        </button>
                        <div className="h-4 w-px bg-purple-200 mx-1" />
                        <span className="text-gray-600">Color</span>
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => applyDescriptionFormat("foreColor", "#111827")}
                          className="h-5 w-5 rounded-full border border-gray-300 bg-gray-900"
                          aria-label="Text color dark"
                        />
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => applyDescriptionFormat("foreColor", "#6B21A8")}
                          className="h-5 w-5 rounded-full border border-gray-300 bg-purple-700"
                          aria-label="Text color purple"
                        />
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => applyDescriptionFormat("foreColor", "#1D4ED8")}
                          className="h-5 w-5 rounded-full border border-gray-300 bg-blue-600"
                          aria-label="Text color blue"
                        />
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => applyDescriptionFormat("foreColor", "#B91C1C")}
                          className="h-5 w-5 rounded-full border border-gray-300 bg-red-600"
                          aria-label="Text color red"
                        />
                      </div>
                    </div>
                    <div
                      className="min-h-[110px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      contentEditable
                      suppressContentEditableWarning
                      onInput={() => {
                        const html = descriptionRef.current?.innerHTML || "";
                        setFormData((prev) => ({
                          ...prev,
                          description: html,
                        }));
                      }}
                      ref={descriptionRef}
                      dangerouslySetInnerHTML={{ __html: formData.description }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Product Details
                    </label>
                    <textarea
                      name="details"
                      value={formData.details}
                      onChange={handleChange}
                      rows="5"
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>

          <div className="p-6 border-t">
            <AccessoryTab productId={formData._id} />
          </div>

        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white px-6 py-4 border-t flex justify-end gap-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          {saveError && (
            <div className="text-sm text-red-600 self-center">
              {saveError}
            </div>
          )}
          <button
            type="submit"
            form="edit-product-form"
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditModal;
